import React from 'react';
import {Icon, Popup} from 'semantic-ui-react';

import {getLink} from '@appComponents/Link';
import {CompetitorsName} from '@modules/events/views/common/CompetitorsName';
import {CompetitorsAndTypeWrapper} from '@utils/CompetitorsAndTypeWrapper';
import {
    ArgosStatus,
    getArgosJobStatusChipColor,
    getArgosStreamStatusChipColor,
} from '@utils/tableHelpers';
import {
    buildEventIdPopupRow,
    buildMatchIdPopupRow,
    buildNotesPopupRow,
    buildPropertyLicencePopupRow,
    buildStageIdPopupRow,
    renderEventSecondLine,
    renderFirstLinePopup,
} from '@modules/liveControl/components/LiveControlTable/renderEventContentShared';

import ArgosJobLiveControlActions from '../ArgosJobLiveControlActions';
import {ArgosJobEndTime, ArgosJobStartTime} from './ArgosJobTime';
import {ArgosTunnelCell} from './ArgosTunnelCell';
import {renderArgosEventTimeCell} from './utils';

const eventRowForCompetitors = (row) => ({
    event_id: row.event_id,
    event_content_id: row.event_id,
    home_team_name: row.home_team_name,
    away_team_name: row.away_team_name,
});

const eventRowForAvatar = (row) => ({
    id: String(row.event_id),
    event_id: row.event_id,
    event_type_id: row.event_type_id,
    sport_id: row.sport_id ?? null,
    sport_name: row.sport_name ?? null,
    tournament_category_name: row.tournament_category_name ?? null,
    tournament_name: row.tournament_name ?? null,
    sport_has_court: !!row.sport_has_court,
    sport_has_tv_channel: !!row.sport_has_tv_channel,
    stadium_name: row.stadium_name ?? null,
    tv_channels: row.tv_channels ?? [],
    content_category_level_1_name: row.content_category_level_1_name ?? null,
    content_category_level_2_name: row.content_category_level_2_name ?? null,
    content_category_level_3_name: row.content_category_level_3_name ?? null,
});

const renderArgosEventContent = (row) => {
    const eventLink = `/events/${row.event_id}/argos-jobs#argos-job-${row.id}`;

    const contentToRender = (
        <div>
            <CompetitorsAndTypeWrapper>
                <a href={eventLink} target='_blank' rel='noopener noreferrer'>
                    <CompetitorsName event={eventRowForCompetitors(row)} renderLink={false}/>
                </a>
                <span className='--marginLeft-0-5-em'>({row.event_id})</span>
                {row.description ? <span>{`\u00A0- ${row.description}`}</span> : null}
            </CompetitorsAndTypeWrapper>
        </div>
    );

    const popupRows = [
        buildPropertyLicencePopupRow(row),
        buildNotesPopupRow(row),
        buildEventIdPopupRow(row),
        buildMatchIdPopupRow(row),
        buildStageIdPopupRow(row),
    ];

    const firstLine = renderFirstLinePopup({
        rowId: row.id,
        keyPrefix: 'argos-event-first',
        trigger: contentToRender,
        rows: popupRows,
    });

    return [
        firstLine,
        renderEventSecondLine(eventRowForAvatar(row), row.id, 'argos-event-second'),
    ];
};

const renderEntryPointCell = (row) => {
    return (
        <Popup
            trigger={
                <a
                    href={getLink('srtOriginEntryPoints.edit', {id: row.srt_origin_entry_point_id})}
                    target='_blank'
                    rel='noopener noreferrer'
                >
                    {row.srt_origin_entry_point_name}
                </a>
            }
            content={`URL: ${row.srt_origin_entry_point_url ?? '-'}`}
        />
    );
};

const renderInfoIcon = (row) => {
    if (!row.message) {
        return null;
    }

    return (
        <Popup
            trigger={<Icon name='info circle' color='blue' className='--marginLeft-0-5-em'/>}
            content={row.message}
        />
    );
};

export const argosJobLiveControlTableRowRenderer = (column, row, refetch) => {
    switch (column) {
        case 'event_start_datetime':
            return renderArgosEventTimeCell(row);
        case 'event':
            return renderArgosEventContent(row);
        case 'argos_job_definition':
            return (
                <Popup
                    trigger={<span>{row.argos_job_definition_name}</span>}
                    content={row.argos_job_definition_id_string}
                />
            );
        case 'argos_job_region':
            return (
                <Popup
                    trigger={<span>{row.argos_job_region_name}</span>}
                    content={row.argos_job_region_id_string}
                />
            );
        case 'srt_origin_entry_point':
            return renderEntryPointCell(row);
        case 'third_party_client_tunnel':
            return <ArgosTunnelCell row={row}/>;
        case 'job_start_time':
            return <ArgosJobStartTime row={row}/>;
        case 'job_end_time':
            return <ArgosJobEndTime row={row}/>;
        case 'job_status': {
            const rawStatus = row.job_uuid ? row.job_status : null;
            const label = rawStatus
                ? String(rawStatus).replaceAll('_', ' ')
                : 'Not started';
            const hasExitCodePopup = !!rawStatus && (
                (null != row.exit_code && 0 !== row.exit_code)
                || (null != row.exit_code_description && '' !== row.exit_code_description)
            );
            const statusChip = rawStatus
                ? <ArgosStatus
                    label={label}
                    color={getArgosJobStatusChipColor(String(rawStatus).toLowerCase())}
                />
                : label;
            const chipWithTooltip = hasExitCodePopup
                ? (
                    <Popup
                        trigger={<span>{statusChip}</span>}
                        content={(
                            <div className='argos-exit-code-tooltip'>
                                <div>{`Exit code: ${row.exit_code ?? '-'}`}</div>
                                <div>{`Exit code description: ${row.exit_code_description ?? '-'}`}</div>
                            </div>
                        )}
                    />
                )
                : statusChip;

            return (
                <span>
                    {chipWithTooltip}
                    {rawStatus ? renderInfoIcon(row) : null}
                </span>
            );
        }
        case 'stream_status': {
            const rawStatus = row.job_uuid ? row.stream_status : null;

            if (!rawStatus) {
                return <span>-</span>;
            }

            const label = String(rawStatus).replaceAll('_', ' ');

            return <ArgosStatus label={label} color={getArgosStreamStatusChipColor(String(rawStatus).toLowerCase())} />;
        }
        case 'actions':
            return <ArgosJobLiveControlActions row={row} refetch={refetch}/>;
        default:
            return null;
    }
};
